import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { CheckCircle, XCircle, Phone, User, Search, Trash2, Loader2, Crown, Download, Wallet, ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { getAuthToken } from "../../lib/api";
import api from "../../lib/api";
import AdminResetPasswordButton from "./AdminResetPasswordButton";
import AdminAssistPassengerButton from "./AdminAssistPassengerButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_STYLES = { admin: "bg-[#EF4444]/10 text-[#EF4444]", driver: "bg-[#0A2540]/10 text-[#0A2540]", passenger: "bg-[#FACC15]/20 text-[#0A2540]" };

function getAuthImgUrl(docPath) {
  if (!docPath) return null;
  const token = getAuthToken();
  return `${BACKEND_URL}/api/files/${docPath}?token=${token}`;
}

const ALL_PERMISSIONS = [
  { key: "create", label: "Creer" },
  { key: "edit", label: "Editer" },
  { key: "delete", label: "Supprimer" },
  { key: "validate", label: "Valider chauffeurs" },
  { key: "manage_tft", label: "Gerer TFT" },
  { key: "manage_users", label: "Gerer utilisateurs" },
  { key: "export", label: "Exporter" },
];

export function UserFilters({ userFilter, setUserFilter, userRoleFilter, setUserRoleFilter, onExport }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
        <Input value={userFilter} onChange={(e) => setUserFilter(e.target.value)} placeholder="Rechercher par nom ou email..." className="pl-10 h-9 border-[#CBD5E1]" data-testid="user-search-filter" />
      </div>
      <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
        <SelectTrigger className="w-40 h-9 border-[#CBD5E1]" data-testid="user-role-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous les roles</SelectItem>
          <SelectItem value="passenger">Passagers</SelectItem>
          <SelectItem value="driver">Chauffeurs</SelectItem>
          <SelectItem value="admin">Admins</SelectItem>
        </SelectContent>
      </Select>
      <Button size="sm" variant="outline" onClick={onExport} className="border-[#CBD5E1] h-9" data-testid="export-users-csv">
        <Download className="w-3 h-3 mr-1" /> Export CSV
      </Button>
    </div>
  );
}

function formatRegistrationDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mn = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yy} ${hh}:${mn}`;
  } catch { return "—"; }
}

function UserRow({ u, onToggle, onDelete, isSuperAdmin, onPromote, onDemote, onEditPaymentMethods }) {
  const isActive = u.is_active !== false;
  const isSubAdmin = u.role === "admin" && !u.is_super_admin;
  return (
    <tr className="border-b border-[#CBD5E1] last:border-0" data-testid={`admin-user-${u.id}`}>
      <td className="p-3">
        <div className="flex items-center gap-2">
          {u.profile_photo ? (
            <img src={getAuthImgUrl(u.profile_photo)} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#0A2540]/10 flex items-center justify-center"><User className="w-4 h-4 text-[#64748B]" /></div>
          )}
          <div>
            <span className="text-sm text-[#0A2540] font-medium flex items-center gap-1">
              {u.name}
              {u.is_super_admin && <Crown className="w-3 h-3 text-[#FACC15]" />}
              {isSubAdmin && <Crown className="w-3 h-3 text-[#64748B]" />}
            </span>
            {isSubAdmin && u.admin_permissions?.length > 0 && (
              <p className="text-[10px] text-[#64748B]">{u.admin_permissions.join(", ")}</p>
            )}
          </div>
        </div>
      </td>
      <td className="p-3">
        <p className="text-xs text-[#64748B]">{u.email}</p>
        <p className="text-xs text-[#64748B] flex items-center gap-1"><Phone className="w-3 h-3" /> {u.phone || "N/A"}</p>
      </td>
      <td className="p-3">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_STYLES[u.role] || ROLE_STYLES.passenger}`}>{u.role}{isSubAdmin ? " (sous)" : ""}</span>
      </td>
      <td className="p-3">
        <span className="text-xs text-[#0A2540] flex items-center gap-1" data-testid={`user-registered-${u.id}`}>
          <Calendar className="w-3 h-3 text-[#64748B]" />
          {formatRegistrationDate(u.created_at)}
        </span>
      </td>
      <td className="p-3">
        {isActive ? (
          <span className="text-xs text-[#10B981] font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Actif</span>
        ) : (
          <span className="text-xs text-[#EF4444] font-medium flex items-center gap-1"><XCircle className="w-3 h-3" /> Inactif</span>
        )}
      </td>
      <td className="p-3">
        <div className="flex gap-1 flex-wrap">
          {!u.is_super_admin && (
            <>
              {u.role !== "admin" && isSuperAdmin && (
                <Button size="sm" variant="outline" onClick={() => onPromote(u)} className="text-xs border-[#FACC15] text-[#0A2540] h-7" data-testid={`promote-user-${u.id}`}>
                  <Crown className="w-3 h-3 mr-0.5" /> Sous-admin
                </Button>
              )}
              {isSubAdmin && isSuperAdmin && (
                <Button size="sm" variant="outline" onClick={() => onDemote(u.id)} className="text-xs border-[#64748B] text-[#64748B] h-7" data-testid={`demote-user-${u.id}`}>
                  Retirer admin
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => onToggle(u.id, isActive)} className="text-xs border-[#CBD5E1] h-7" data-testid={`toggle-user-${u.id}`}>
                {isActive ? "Desactiver" : "Activer"}
              </Button>
              {u.role === "passenger" && (
                <Button size="sm" variant="outline" onClick={() => onEditPaymentMethods(u)} className="text-xs border-[#FACC15] text-[#0A2540] h-7" data-testid={`edit-payment-methods-${u.id}`} title="Configurer les moyens de paiement">
                  <Wallet className="w-3 h-3 mr-0.5" /> Paiement
                </Button>
              )}
              <AdminAssistPassengerButton user={u} />
              <AdminResetPasswordButton user={u} />
              <Button size="sm" variant="ghost" onClick={() => onDelete(u)} className="h-7 px-2 text-xs text-[#EF4444] hover:text-[#EF4444]" data-testid={`delete-user-${u.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function PromoteDialog({ user: promoteUser, onClose, onRefresh }) {
  const [selectedPerms, setSelectedPerms] = useState(promoteUser?.admin_permissions || []);
  const [loading, setLoading] = useState(false);

  const togglePerm = (key) => {
    setSelectedPerms(prev => prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]);
  };

  const handlePromote = async () => {
    if (!promoteUser) return;
    setLoading(true);
    try {
      await api.post(`/admin/promote-sub-admin/${promoteUser.id}`, { permissions: selectedPerms });
      onClose();
      onRefresh?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={!!promoteUser} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="promote-dialog">
        <DialogHeader><DialogTitle className="text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Promouvoir en sous-administrateur</DialogTitle></DialogHeader>
        {promoteUser && (
          <div className="space-y-4">
            <div className="bg-[#F8FAFC] rounded-lg p-3 border border-[#CBD5E1]">
              <p className="text-sm font-semibold text-[#0A2540]">{promoteUser.name}</p>
              <p className="text-xs text-[#64748B]">{promoteUser.email}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-[#0A2540] mb-2">Choisir les permissions :</p>
              <div className="space-y-2">
                {ALL_PERMISSIONS.map((p) => (
                  <label key={p.key} className="flex items-center gap-2 cursor-pointer" data-testid={`perm-${p.key}`}>
                    <input type="checkbox" checked={selectedPerms.includes(p.key)} onChange={() => togglePerm(p.key)} className="rounded border-[#CBD5E1] text-[#0A2540] focus:ring-[#0A2540]" />
                    <span className="text-sm text-[#0A2540]">{p.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={handlePromote} disabled={loading || selectedPerms.length === 0} className="w-full bg-[#0A2540] text-white hover:bg-[#081b2e] font-semibold" data-testid="confirm-promote-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Crown className="w-4 h-4 mr-1" />}
              Promouvoir ({selectedPerms.length} permissions)
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PaymentMethodsDialog({ user: editUser, onClose, onRefresh }) {
  const ALL = [
    { key: "tft", label: "Portefeuille TFT" },
    { key: "mtn", label: "Recharge MTN Mobile Money" },
    { key: "orange", label: "Recharge Orange Money" },
  ];
  const [disabled, setDisabled] = useState(editUser?.disabled_payment_methods || []);
  const [saving, setSaving] = useState(false);

  const toggle = (key) => {
    setDisabled((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleSave = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      await api.put(`/admin/users/${editUser.id}/payment-methods`, { disabled });
      onClose();
      onRefresh?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!editUser} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="payment-methods-dialog">
        <DialogHeader><DialogTitle className="text-[#0A2540] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Wallet className="w-4 h-4" /> Moyens de paiement</DialogTitle></DialogHeader>
        {editUser && (
          <div className="space-y-4">
            <div className="bg-[#F8FAFC] rounded-lg p-3 border border-[#CBD5E1]">
              <p className="text-sm font-semibold text-[#0A2540]">{editUser.name}</p>
              <p className="text-xs text-[#64748B]">{editUser.email}</p>
            </div>
            <p className="text-xs text-[#64748B]">Cochez les méthodes à <strong>désactiver</strong> pour ce passager uniquement (override sur la config globale). Les méthodes désactivées seront masquées dans son interface.</p>
            <div className="space-y-2">
              {ALL.map(({ key, label }) => {
                const isOff = disabled.includes(key);
                return (
                  <label key={key} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-[#F8FAFC]" data-testid={`pm-toggle-${key}`}>
                    <input type="checkbox" checked={isOff} onChange={() => toggle(key)} className="rounded border-[#CBD5E1] text-[#EF4444] focus:ring-[#EF4444]" />
                    <span className={`text-sm flex-1 ${isOff ? "text-[#EF4444] line-through" : "text-[#0A2540]"}`}>{label}</span>
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${isOff ? "bg-[#EF4444]/10 text-[#EF4444]" : "bg-[#10B981]/10 text-[#10B981]"}`}>
                      {isOff ? "Désactivé" : "Autorisé"}
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1" data-testid="cancel-payment-methods-btn">Annuler</Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#0A2540] text-white hover:bg-[#081b2e]" data-testid="save-payment-methods-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />} Enregistrer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function UsersTable({
  users,
  onToggle,
  onDelete,
  isSuperAdmin,
  onRefresh,
  loading = false,
  total = 0,
  page = 1,
  pageSize = 50,
  totalPages = 0,
  onPageChange,
  onPageSizeChange,
}) {
  const [promoteDialog, setPromoteDialog] = useState(null);
  const [paymentMethodsDialog, setPaymentMethodsDialog] = useState(null);

  const handleDemote = async (userId) => {
    try {
      await api.post(`/admin/demote-admin/${userId}`);
      onRefresh?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    }
  };

  const hasPagination = typeof onPageChange === "function" && totalPages > 0;
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <>
      <div className="bg-white rounded-xl border border-[#CBD5E1] overflow-hidden">
        <div className="overflow-x-auto relative">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm" data-testid="users-loading-overlay">
              <Loader2 className="w-6 h-6 animate-spin text-[#0A2540]" />
            </div>
          )}
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#CBD5E1] bg-[#F8FAFC]">
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Utilisateur</th>
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Contact</th>
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Role</th>
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Inscrit le</th>
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Statut</th>
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <UserRow
                  key={u.id}
                  u={u}
                  onToggle={onToggle}
                  onDelete={onDelete}
                  isSuperAdmin={isSuperAdmin}
                  onPromote={setPromoteDialog}
                  onDemote={handleDemote}
                  onEditPaymentMethods={setPaymentMethodsDialog}
                />
              ))}
              {users.length === 0 && !loading && (
                <tr><td colSpan="6" className="p-6 text-center text-sm text-[#64748B]" data-testid="users-empty-state">Aucun utilisateur trouvé.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-3 py-2 border-t border-[#CBD5E1] bg-[#F8FAFC] flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="text-xs text-[#64748B]" data-testid="users-total-label">
            {hasPagination ? (
              <>Affichage <strong className="text-[#0A2540]">{rangeStart}-{rangeEnd}</strong> sur <strong className="text-[#0A2540]" data-testid="users-total-count">{total.toLocaleString()}</strong> utilisateur(s)</>
            ) : (
              <>{users.length} utilisateur(s) affiche(s)</>
            )}
          </div>
          {hasPagination && (
            <div className="flex items-center gap-2">
              <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange?.(parseInt(v, 10))}>
                <SelectTrigger className="h-8 w-28 text-xs border-[#CBD5E1]" data-testid="users-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                  <SelectItem value="100">100 / page</SelectItem>
                  <SelectItem value="200">200 / page</SelectItem>
                  <SelectItem value="500">500 / page</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPageChange(Math.max(1, page - 1))} className="h-8 border-[#CBD5E1]" data-testid="users-prev-page">
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                <span className="text-xs text-[#0A2540] px-2 tabular-nums" data-testid="users-page-indicator">
                  Page {page} / {totalPages || 1}
                </span>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => onPageChange(Math.min(totalPages, page + 1))} className="h-8 border-[#CBD5E1]" data-testid="users-next-page">
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <PromoteDialog user={promoteDialog} onClose={() => setPromoteDialog(null)} onRefresh={onRefresh} />
      <PaymentMethodsDialog user={paymentMethodsDialog} onClose={() => setPaymentMethodsDialog(null)} onRefresh={onRefresh} />
    </>
  );
}

export function DeleteConfirmDialog({ user, onConfirm, onCancel, loading }) {
  return (
    <Dialog open={!!user} onOpenChange={() => onCancel()}>
      <DialogContent className="sm:max-w-sm" data-testid="delete-confirm-dialog">
        <DialogHeader><DialogTitle className="text-[#EF4444]">Confirmer la suppression</DialogTitle></DialogHeader>
        <div className="py-4">
          <p className="text-sm text-[#0A2540]">Etes-vous sur de vouloir supprimer <strong>{user?.name}</strong> ({user?.email}) ?</p>
          <p className="text-xs text-[#64748B] mt-2">Cette action est irreversible.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} className="flex-1">Annuler</Button>
          <Button onClick={() => onConfirm(user?.id)} disabled={loading} className="flex-1 bg-[#EF4444] text-white hover:bg-[#DC2626]" data-testid="confirm-delete-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />} Supprimer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
